import { format } from 'date-fns';
import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useGetRevenueTrend } from '~entities/dashboard/api/get-revenue-trend';

import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';

interface RevenueTrendCSVDownloadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  startDate: string;
  endDate: string;
}

export function RevenueTrendCSVDownload({
  sessionId,
  startDate,
  endDate,
  open,
  onOpenChange,
}: RevenueTrendCSVDownloadProps) {
  const [csvDates, setCSVDates] = useState({
    startDate: startDate,
    endDate: endDate,
  });

  const { isFetching: isCsvFetching, refetch: refetchCsvData } =
    useGetRevenueTrend({
      sessionId,
      startDate: csvDates.startDate,
      endDate: csvDates.endDate,
      csv: true,
    });

  const handleDownloadCSV = async () => {
    try {
      const { data } = await refetchCsvData();

      if (typeof data !== 'string') {
        throw new Error('Expected string response but received invalid data');
      }
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue-trend-${csvDates.startDate}-to-${csvDates.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV downloaded successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='space-y-4'>
        <DialogHeader>
          <DialogTitle>Download CSV Report</DialogTitle>
          <DialogDescription>
            Download a CSV report of the revenue trend for the selected date
            range.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3'>
          <div className='flex flex-col space-y-2'>
            <Label>Start Date</Label>
            <DateTimePicker
              value={new Date(csvDates.startDate)}
              onChange={(date) => {
                setCSVDates({
                  ...csvDates,
                  startDate: date ? format(date, 'yyyy-MM-dd') : startDate,
                });
              }}
              granularity='day'
              displayFormat={{ hour24: 'PPP' }}
            />
          </div>

          <div className='flex flex-col space-y-2'>
            <Label>End Date</Label>
            <DateTimePicker
              value={new Date(csvDates.endDate)}
              onChange={(date) => {
                setCSVDates({
                  ...csvDates,
                  endDate: date ? format(date, 'yyyy-MM-dd') : endDate,
                });
              }}
              granularity='day'
              displayFormat={{ hour24: 'PPP' }}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button onClick={handleDownloadCSV} disabled={isCsvFetching}>
            {isCsvFetching ? (
              <Loader2 className='animate-spin' />
            ) : (
              <Download />
            )}
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
